import { createPortal } from 'react-dom'
import { HexColorPicker, HexColorInput } from 'react-colorful'
import { styled } from 'baseui'
import { LabelLarge } from 'baseui/typography'
import { lightTheme } from '@/customTheme'
import { useTranslation } from 'react-i18next'
import { forwardRef, useEffect, useRef, useState } from 'react'
import './colorButton.css'

const WrapColorPicker = styled('div', props => ({
  width: '100%',
  padding: '0 16px',
}))

interface ColorPickerPopoverProps {
  isOpen: boolean
  position: { top: number; left: number }
  color: string
  onColorChange: (color: string) => void
  onColorFinalChange?: (color: string) => void
}

const ColorPickerPopover = forwardRef<HTMLDivElement, ColorPickerPopoverProps>((props, ref) => {
  const { t } = useTranslation()
  const draggingRef = useRef(false)
  const lastColorRef = useRef(props.color) // Emit final change color
  const { onColorFinalChange } = props
  const [uiColor, setUiColor] = useState(props.color)

  useEffect(() => {
    lastColorRef.current = props.color
    if (!draggingRef.current) {
      setUiColor(props.color)
    }
  }, [props.color])

  useEffect(() => {
    const commit = () => {
      if (!draggingRef.current) return
      draggingRef.current = false
      if (lastColorRef.current) {
        onColorFinalChange?.(lastColorRef.current)
      }
    }
    window.addEventListener('pointerup', commit)
    window.addEventListener('mouseup', commit)
    window.addEventListener('touchend', commit)
    return () => {
      window.removeEventListener('pointerup', commit)
      window.removeEventListener('mouseup', commit)
      window.removeEventListener('touchend', commit)
    }
  }, [onColorFinalChange])
  
  if (!props.isOpen || !props.position) return null

  return createPortal(
    <div
      ref={ref}
      id="color-picker-popover"
      style={{
        position: 'fixed',
        left: `${props.position.left}px`,
        top: `${props.position.top}px`,
        background: '#ffffff',
        width: '292px',
        padding: '0px 0px 16px',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)',
        borderRadius: '16px',
        zIndex: 9999,
      }}
    >
      <LabelLarge
        $style={{
          height: '26px',
          padding: '0 0 0 16px',
          margin: '16px 0 8px',
          display: 'flex',
          alignItems: 'center',
          letterSpacing: '-0.011em',
          color: lightTheme.colors.blackGray,
        }}
      >
        {t('Custom color')}
      </LabelLarge>
      <WrapColorPicker className="wrap-color-picker" >
        <HexColorPicker
          color={uiColor}
          onChange={c => {
            draggingRef.current = true
            lastColorRef.current = c
            setUiColor(c)
            props.onColorChange(c)
          }}
        />
        <div
          className="wrap-input"
          style={{
            position: 'relative',
            marginTop: '16px',
          }}
        >
          <HexColorInput
            color={uiColor}
            onClick={e => {
              e.currentTarget.select()
            }}
            onChange={c => {
              draggingRef.current = true
              lastColorRef.current = c
              setUiColor(c)
              props.onColorChange(c)
            }}
            onBlur={() => {
              draggingRef.current = false
              props.onColorFinalChange?.(lastColorRef.current)
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                draggingRef.current = false
                props.onColorFinalChange?.(lastColorRef.current)
              }
            }}
          />
          <div
            className="display-color"
            style={{
              background: `${uiColor}`,
              position: 'absolute',
              width: '32px',
              height: '32px',
              right: '4px',
              top: '4px',
              borderRadius: '8px',
            }}
          ></div>
        </div>
      </WrapColorPicker>
    </div>,
    document.body
  )
})

ColorPickerPopover.displayName = 'ColorPickerPopover'

export default ColorPickerPopover
